import { sva } from '@allenai/varnish-panda-runtime/css';
import type { RecipeVariantProps } from '@allenai/varnish-panda-runtime/types';

const selectRecipe = sva({
    slots: [
        'root',
        'button',
        'arrowIcon',
        'listBox',
        'listBoxSection',
        'listBoxHeader',
        'listBoxItem',
        'listBoxItemIcon',
        'listBoxItemText',
        'popover',
        'selectValue',
        'label',
        'description',
        'error',
    ],
    base: {
        root: {
            boxSizing: 'border-box',
        },
        button: {
            cursor: 'pointer',
            fontWeight: 'medium',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '[fit-content]',
            transition: '[background-color 250ms, color 250ms, opacity 250ms]',
            _disabled: {
                color: 'text.disabled',
            },
        },
        popover: {
            marginTop: '0',
            // keep these?:
            '&[data-placement=top]': {
                paddingBottom: '[1px]', // offset to show button's border
            },
            '&[data-placement=bottom]': {
                paddingTop: '[1px]', // offset to show button's border
            },
            color: 'text',
        },
        listBox: {
            backgroundColor: {
                base: 'white',
                _dark: 'background',
            },
            display: 'grid',
            // This min and the `listBoxItem` `paddingInlineEnd` need to visually match
            gridTemplateAreas: '"icon text"',
            gridTemplateColumns: '[minmax(8px, min-content) auto]',
            minWidth: '[100%]',
            padding: '0',
            justifySelf: 'center',
            '[data-placement=top] &': {
                borderTopRadius: 'md',
            },
            '[data-placement=bottom] &': {
                borderBottomRadius: 'md',
            },
        },
        listBoxSection: {
            // pass through subgrid
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: 'icon / text',
            minWidth: '[100%]',
        },
        listBoxItem: {
            // subgrid
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: 'icon / text',
            alignItems: 'center',
            gap: '[8px]',
            padding: '[8px]',
            // this needs to visually match the `listBox` grid collapsing (which will visually be the left padding)
            paddingInlineEnd: '[12px]',
            cursor: 'pointer',

            // this makes all items have rounded hover and focus
            borderRadius: 'md',

            _active: {
                backgroundColor: 'background',
            },
            _hover: {
                backgroundColor: 'background.opacity-10.reversed', // form/menu/hovered/bg
            },
        },
        listBoxItemIcon: {
            gridColumn: 'icon',
            justifySelf: 'center',
            '& svg': {
                width: '[100%]',
                height: '[100%]',
            },
        },
        listBoxItemText: {
            gridColumn: 'text',
        },
        arrowIcon: {
            color: 'colorPalette.contrast',
            pointerEvents: 'none',
            transition: '[transform 250ms]',
            _groupDisabled: {
                color: 'gray.80',
            },
            _groupOpen: {
                transform: 'rotate(180deg)',
            },
        },
        selectValue: {
            display: 'flex',
            gap: '[8px]',
            alignItems: 'center',

            _groupInvalid: {
                color: 'text.error',
            },
        },
        listBoxHeader: {
            fontSize: 'md',
            border: '[none]',
            padding: '[8px 16px]',
            gridColumn: 'icon / text',
        },
        label: {},
        description: {},
        error: {},
    },
    variants: {
        fullWidth: {
            false: {},
            true: {
                root: {
                    width: '[100%]',
                },
                button: {
                    width: '[100%]',
                },
            },
        },
        popoverAutoWidth: {
            true: {
                popover: {
                    minWidth: 'var(--trigger-width)',
                },
            },
        },
        variant: {
            contained: {},
            outlined: {},
        },
        shape: {
            box: {
                button: {
                    borderRadius: 'sm',
                },
                popover: {
                    paddingInline: '[4px]',
                },
            },
            rounded: {
                button: {
                    borderRadius: 'full',
                },
                popover: {
                    paddingInline: '[18px]', // 16px?
                },
            },
        },
        size: {
            small: {
                root: {
                    fontSize: '[12px]', // todo: add fontsize for xs
                },
                button: {
                    padding: '[8px 20px]',
                },
                listBoxItem: {
                    fontSize: '[12px]', // todo: add fontsize for xs
                    lineHeight: '[14px]',
                },
                listBoxItemIcon: {
                    width: '[16px]',
                    height: '[16px]',
                },
            },
            medium: {
                root: {
                    fontSize: 'sm',
                },
                button: {
                    padding: '[8px 24px]',
                },
                listBoxItem: {
                    fontSize: 'sm',
                    lineHeight: '[22px]',
                },
                listBoxItemIcon: {
                    width: '[20px]',
                    height: '[20px]',
                },
            },
            large: {
                root: {
                    fontSize: 'md',
                },
                button: {
                    padding: '[16px 24px]',
                },
                listBoxItem: {
                    fontSize: 'md',
                    lineHeight: '[24px]',
                },
                listBoxItemIcon: {
                    width: '[24px]',
                    height: '[24px]',
                },
            },
        },
    },
    defaultVariants: {
        variant: 'outlined',
        shape: 'box',
        size: 'medium',
        popoverAutoWidth: true,
    },
});

type SelectRecipeProps = RecipeVariantProps<typeof selectRecipe>;

export { selectRecipe, selectRecipe as default };
export type { SelectRecipeProps };
